import axios from "axios";
import { buildAssistantUrl } from "./assistantApi";
import {
  getConversation,
  upsertConversationSnapshot,
  ASSISTANT_SESSION_KEY,
  ASSISTANT_TAB_INSTANCE_KEY,
} from "./assistantStore";
import { createRandomId } from "./randomId";

const CLIENT_ID_PREFIX =
  import.meta.env.VITE_ABLY_CLIENT_ID_PREFIX ?? "hpoll-user";

let inflightSessionPromise = null;
let runtimeTabId = null;
let tabInstanceId = null;
let tabUniquenessPromise = null;
let duplicateSourceConversationId = null;
let tabBroadcastChannel = null;

const TAB_CHANNEL_NAME = "harris_ai_assistant_tab_channel";

const generateConversationId = () => createRandomId("conv");

const generateTabInstanceId = () => createRandomId("tab");

const getRuntimeTabId = () => {
  if (!runtimeTabId) {
    runtimeTabId = generateTabInstanceId();
  }
  return runtimeTabId;
};

const ensureTabInstanceId = () => {
  if (typeof window === "undefined") return null;
  if (tabInstanceId) return tabInstanceId;
  try {
    const stored = window.sessionStorage.getItem(ASSISTANT_TAB_INSTANCE_KEY);
    if (stored) {
      tabInstanceId = stored;
      return tabInstanceId;
    }
    const next = generateTabInstanceId();
    window.sessionStorage.setItem(ASSISTANT_TAB_INSTANCE_KEY, next);
    tabInstanceId = next;
    return tabInstanceId;
  } catch (error) {
    console.warn("[AI Assistant] Failed to persist tab instance", error);
    tabInstanceId = generateTabInstanceId();
    return tabInstanceId;
  }
};

const resetTabInstanceId = () => {
  if (typeof window === "undefined") return null;
  const next = generateTabInstanceId();
  try {
    window.sessionStorage.setItem(ASSISTANT_TAB_INSTANCE_KEY, next);
  } catch (error) {
    console.warn("[AI Assistant] Failed to reset tab instance", error);
  }
  tabInstanceId = next;
  return next;
};

const ensureTabChannel = () => {
  if (typeof window === "undefined") return null;
  if (typeof BroadcastChannel === "undefined") return null;
  if (tabBroadcastChannel) return tabBroadcastChannel;
  ensureTabInstanceId();
  tabBroadcastChannel = new BroadcastChannel(TAB_CHANNEL_NAME);
  tabBroadcastChannel.addEventListener("message", (event) => {
    const data = event?.data || {};
    if (data.type !== "tab-ping") return;
    if (data.tabId !== tabInstanceId) return;
    if (data.senderId === getRuntimeTabId()) return;
    tabBroadcastChannel.postMessage({
      type: "tab-pong",
      tabId: tabInstanceId,
      nonce: data.nonce,
      senderId: getRuntimeTabId(),
    });
  });
  return tabBroadcastChannel;
};

const detectDuplicateTab = async () => {
  if (typeof window === "undefined") return false;
  const currentTabId = ensureTabInstanceId();
  if (!currentTabId) return false;
  const channel = ensureTabChannel();
  if (!channel) return false;
  const senderId = getRuntimeTabId();
  const nonce = createRandomId("nonce");

  return new Promise((resolve) => {
    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;
      channel.removeEventListener("message", onMessage);
      clearTimeout(timeoutId);
    };
    const onMessage = (event) => {
      const data = event?.data || {};
      if (
        data.type === "tab-pong" &&
        data.tabId === currentTabId &&
        data.nonce === nonce &&
        data.senderId !== senderId
      ) {
        cleanup();
        resolve(true);
      }
    };
    const timeoutId = setTimeout(() => {
      cleanup();
      resolve(false);
    }, 160);
    channel.addEventListener("message", onMessage);
    channel.postMessage({
      type: "tab-ping",
      tabId: currentTabId,
      nonce,
      senderId,
    });
  });
};

const readSessionStorageIdentity = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(ASSISTANT_SESSION_KEY);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string" && parsed.trim()) {
        return { clientId: null, conversationId: parsed.trim() };
      }
      if (parsed && typeof parsed === "object") {
        const conversationId =
          parsed.conversationId ||
          parsed.conversation_id ||
          parsed.sessionId ||
          null;
        const clientId = parsed.clientId || parsed.client_id || null;
        if (conversationId) {
          return { clientId, conversationId };
        }
      }
    } catch (parseError) {
      // Legacy fallback: raw might already be the conversation id string.
      if (typeof raw === "string" && raw.trim()) {
        return { clientId: null, conversationId: raw.trim() };
      }
      throw parseError;
    }
  } catch (error) {
    console.warn("[AI Assistant] Failed to read session identity", error);
  }
  return null;
};

const ensureUniqueTabSession = async () => {
  if (tabUniquenessPromise) return tabUniquenessPromise;
  tabUniquenessPromise = (async () => {
    const isDuplicate = await detectDuplicateTab();
    if (!isDuplicate) {
      return false;
    }
    console.debug("[AI Assistant] duplicate tab detected; resetting session");
    const storedIdentity = readSessionStorageIdentity();
    duplicateSourceConversationId = storedIdentity?.conversationId || null;
    resetTabInstanceId();
    try {
      window.sessionStorage.removeItem(ASSISTANT_SESSION_KEY);
    } catch (error) {
      console.warn("[AI Assistant] Failed to clear session identity", error);
    }
    return true;
  })();
  return tabUniquenessPromise;
};

const buildClientId = (userId, conversationId) => {
  const safeUserId = (userId ?? "guest").toString().trim() || "guest";
  return `${CLIENT_ID_PREFIX}--${safeUserId}--${conversationId}`;
};

const broadcastSessionUpdate = (clientId, conversationId, meta) => {
  if (typeof window === "undefined") return;
  console.debug("[AI Assistant] session update", {
    clientId,
    conversationId,
    meta,
  });
  window.dispatchEvent(
    new CustomEvent("assistant:session-updated", {
      detail: { clientId, conversationId, meta },
    }),
  );
};

const writeSessionStorageIdentity = ({ clientId, conversationId }) => {
  if (typeof window === "undefined") return;
  if (!clientId || !conversationId) return;
  try {
    window.sessionStorage.setItem(
      ASSISTANT_SESSION_KEY,
      JSON.stringify({ clientId, conversationId }),
    );
  } catch (error) {
    console.warn("[AI Assistant] Failed to persist session identity", error);
  }
};

const safeCloneForStorage = (value, fallback) => {
  if (typeof structuredClone === "function") {
    try {
      return { value: structuredClone(value), sanitized: false };
    } catch (error) {
      // fall through to JSON fallback
    }
  }
  try {
    return { value: JSON.parse(JSON.stringify(value)), sanitized: true };
  } catch (error) {
    return { value: fallback, sanitized: true, failed: true };
  }
};

export const initiateSession = async (userId) => {
  try {
    const authToken = localStorage.getItem("token");
    if (!authToken) {
      throw new Error("auth token missing");
    }

    const conversationId = generateConversationId();
    const clientId = buildClientId(userId, conversationId);

    const initiationResponse = await axios.post(
      buildAssistantUrl("initiatesession/"),
      { clientId, conversationId },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
        },
        withCredentials: true,
      },
    );

    try {
      await upsertConversationSnapshot({
        conversationId,
        clientId,
        messages: [],
        historyMetadata: null,
      });
    } catch (storageError) {
      console.warn("[AI Assistant] Unable to persist session state", storageError);
    }

    broadcastSessionUpdate(clientId, conversationId, { source: "initiateSession" });
    console.debug("[AI Assistant] initiateSession complete", {
      clientId,
      conversationId,
    });
    writeSessionStorageIdentity({ clientId, conversationId });
    return {
      clientId,
      conversationId,
      success: true,
      status: initiationResponse?.status,
    };
  } catch (error) {
    const detail =
      error?.response?.data ||
      error?.message ||
      "Unknown error during initiate session";
    console.error("[AI Assistant] initiateSession failed", detail);
    return { success: false, error: detail };
  }
};

export const cloneSession = async ({
  userId,
  sourceConversationId,
  fallbackToNew = true,
}) => {
  try {
    const authToken = localStorage.getItem("token");
    if (!authToken) {
      throw new Error("auth token missing");
    }

    if (!sourceConversationId) {
      throw new Error("sourceConversationId missing");
    }

    const conversationId = generateConversationId();
    const clientId = buildClientId(userId, conversationId);

    const response = await axios.post(
      buildAssistantUrl("clone-session/"),
      {
        clientId,
        conversationId,
        sourceConversationId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
        },
        withCredentials: true,
      },
    );

    let clonedMessages = [];
    let clonedHistory = null;
    try {
      const sourceConversation = await getConversation(sourceConversationId);
      clonedMessages = sourceConversation?.messages || [];
      clonedHistory = sourceConversation?.historyMetadata ?? null;
    } catch (storageError) {
      console.warn("[AI Assistant] Unable to load source conversation", storageError);
    }

    try {
      await upsertConversationSnapshot({
        conversationId,
        clientId,
        messages: clonedMessages,
        historyMetadata: clonedHistory,
        sourceConversationId,
      });
    } catch (storageError) {
      console.warn("[AI Assistant] Unable to persist cloned session", storageError);
    }

    broadcastSessionUpdate(clientId, conversationId, { source: "cloneSession", sourceConversationId });
    console.debug("[AI Assistant] cloneSession complete", {
      clientId,
      conversationId,
      sourceConversationId,
    });
    writeSessionStorageIdentity({ clientId, conversationId });
    return {
      clientId,
      conversationId,
      sourceConversationId,
      success: true,
      status: response?.status,
    };
  } catch (error) {
    const detail =
      error?.response?.data ||
      error?.message ||
      "Unknown error during clone session";
    console.error("[AI Assistant] cloneSession failed", detail);
    if (fallbackToNew) {
      return initiateSession(userId);
    }
    return { success: false, error: detail };
  }
};

export const getOrCreateSession = async ({
  userId,
  sourceConversationId,
  forceNew = false,
}) => {
  await ensureUniqueTabSession();
  const effectiveSourceConversationId =
    sourceConversationId || duplicateSourceConversationId;
  if (effectiveSourceConversationId) {
    duplicateSourceConversationId = null;
    if (inflightSessionPromise) {
      console.debug("[AI Assistant] session inflight");
      return inflightSessionPromise;
    }
    const sessionPromise = (async () => {
      console.debug("[AI Assistant] session cloning from source (handoff)", {
        sourceConversationId: effectiveSourceConversationId,
      });
      return cloneSession({
        userId,
        sourceConversationId: effectiveSourceConversationId,
        fallbackToNew: true,
      });
    })();
    inflightSessionPromise = sessionPromise;
    try {
      const result = await sessionPromise;
      return result;
    } finally {
      inflightSessionPromise = null;
    }
  }
  if (!forceNew) {
    const storedIdentity = readSessionStorageIdentity();
    if (storedIdentity?.conversationId) {
      const resolvedClientId =
        storedIdentity.clientId ||
        buildClientId(userId, storedIdentity.conversationId);
      console.debug("[AI Assistant] session resolved from sessionStorage", {
        conversationId: storedIdentity.conversationId,
        clientId: resolvedClientId,
      });
      writeSessionStorageIdentity({
        clientId: resolvedClientId,
        conversationId: storedIdentity.conversationId,
      });
      return {
        clientId: resolvedClientId,
        conversationId: storedIdentity.conversationId,
        success: true,
        fromCache: true,
        source: "sessionStorage",
      };
    }
  }
  if (!forceNew && inflightSessionPromise) {
    console.debug("[AI Assistant] session inflight");
    return inflightSessionPromise;
  }
  const sessionPromise = (async () => {
    console.debug("[AI Assistant] session creating new");
    return initiateSession(userId);
  })();
  inflightSessionPromise = sessionPromise;
  try {
    const result = await sessionPromise;
    return result;
  } finally {
    inflightSessionPromise = null;
  }
};

export const saveSessionSnapshot = async ({
  conversationId,
  clientId,
  messages,
  historyMetadata,
  sourceConversationId,
}) =>
  (() => {
    const messageClone = safeCloneForStorage(messages, []);
    const historyClone = safeCloneForStorage(historyMetadata, historyMetadata ?? null);
    if (messageClone.sanitized || historyClone.sanitized) {
      console.warn("[AI Assistant] Stored sanitized session snapshot", {
        messagesSanitized: messageClone.sanitized,
        historySanitized: historyClone.sanitized,
        messagesFailed: messageClone.failed,
        historyFailed: historyClone.failed,
      });
    }
    return upsertConversationSnapshot({
      conversationId,
      clientId,
      messages: messageClone.value,
      historyMetadata: historyClone.value,
      sourceConversationId,
    });
  })()
    .catch((storageError) => {
      console.warn("[AI Assistant] Unable to persist conversation", storageError);
    });

// Removed sendAssistantRequest helper to avoid hidden session creation.
